/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState, useCallback } from 'react';
import { Spinner, Badge, Button } from 'react-bootstrap';
import BookingService from '../../../Services/BookingService';
import { formatDateTime } from '../../../Utils/timezone';

const SOURCE_BADGE_VARIANTS = {
  PU_DASH: 'primary',
  SYSTEM: 'secondary',
  WEBHOOK: 'info',
};

function formatChangeValue(value) {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch (err) {
    return String(value);
  }
}

function VersionHistoryTab({ bookingId }) {
  const [items, setItems] = useState([]);
  const [lastKey, setLastKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const [initialLoaded, setInitialLoaded] = useState(false);

  const fetchPage = useCallback(async (startKey) => {
    try {
      const response = await BookingService.getBookingChanges(bookingId, { startKey });
      // Expected shape: { items: [...], lastKey?: ... }
      if (!response || !Array.isArray(response.items)) {
        // eslint-disable-next-line no-console
        console.warn('Unexpected booking changes response shape:', response);
        return { items: [], lastKey: null };
      }
      return {
        items: response.items,
        lastKey: response.lastKey || null,
      };
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to load booking changes:', err);
      throw err;
    }
  }, [bookingId]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!bookingId) return;
      setLoading(true);
      setError('');
      try {
        const result = await fetchPage(undefined);
        if (cancelled) return;
        setItems(result.items);
        setLastKey(result.lastKey);
      } catch (err) {
        if (cancelled) return;
        setError(err.message || 'Failed to load version history.');
      } finally {
        if (!cancelled) {
          setLoading(false);
          setInitialLoaded(true);
        }
      }
    };
    load();
    return () => { cancelled = true; };
  }, [bookingId, fetchPage]);

  const handleLoadMore = async () => {
    if (!lastKey || loadingMore) return;
    setLoadingMore(true);
    setError('');
    try {
      const result = await fetchPage(lastKey);
      setItems((prev) => [...prev, ...result.items]);
      setLastKey(result.lastKey);
    } catch (err) {
      setError(err.message || 'Failed to load more history.');
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading && !initialLoaded) {
    return (
      <div className="d-flex align-items-center justify-content-center py-4">
        <Spinner animation="border" size="sm" className="me-2" />
        <span className="text-muted">Loading version history…</span>
      </div>
    );
  }

  if (error && items.length === 0) {
    return (
      <div className="alert alert-danger" role="alert">
        <i className="fa fa-exclamation-triangle me-2" />
        {error}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <p className="text-muted">
        <i className="fa fa-info-circle me-2" />
        No change history recorded yet.
      </p>
    );
  }

  return (
    <div>
      <div className="d-flex flex-column gap-2">
        {items.map((entry, idx) => {
          const timestamp = entry.timestamp || entry.Timestamp;
          const changedBy = entry.changedBy || entry.ChangedBy || 'system';
          const source = entry.source || entry.Source;
          const changes = entry.changes || entry.Changes || [];
          const badgeVariant = SOURCE_BADGE_VARIANTS[source] || 'secondary';
          return (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={`${timestamp || 'ts'}-${idx}`}
              className="p-2 border rounded"
            >
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
                <div>
                  <div className="fw-semibold small">
                    {timestamp ? formatDateTime(timestamp) : '—'}
                  </div>
                  <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                    {changedBy}
                  </div>
                </div>
                {source && (
                  <Badge bg={badgeVariant} style={{ fontSize: '0.7rem' }}>
                    {source}
                  </Badge>
                )}
              </div>
              {Array.isArray(changes) && changes.length > 0 ? (
                <ul className="mb-0 ps-3" style={{ fontSize: '0.85rem' }}>
                  {changes.map((change, cIdx) => (
                    <li
                      // eslint-disable-next-line react/no-array-index-key
                      key={`${change.field || change.label || 'c'}-${cIdx}`}
                    >
                      <span className="fw-semibold">{change.label || change.field || 'Field'}</span>
                      {': '}
                      <span className="text-muted">{formatChangeValue(change.oldValue)}</span>
                      {' → '}
                      <span>{formatChangeValue(change.newValue)}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-muted small">No field-level details recorded.</div>
              )}
            </div>
          );
        })}
      </div>

      {error && items.length > 0 && (
        <div className="alert alert-warning mt-3 mb-0 py-2 small" role="alert">
          {error}
        </div>
      )}

      {lastKey && (
        <div className="d-flex justify-content-center mt-3">
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Loading…
              </>
            ) : (
              'Load more'
            )}
          </Button>
        </div>
      )}
    </div>
  );
}

export default VersionHistoryTab;
